/*
 * TabManager — State-Management für offene Projekt-Tabs
 *
 * Verwaltet welche Projekte als Tabs offen sind und welcher aktiv ist.
 * Tab-Reihenfolge und Zustand werden in localStorage persistiert —
 * pro User gescoped, damit auf einem geteilten Browser die Tabs eines
 * anderen Accounts nie sichtbar werden (Privacy).
 */

const TABS_KEY = 'carbisdale_tabs'

export class TabManager {
    /**
     * @param {string} [userId] - ID des eingeloggten Users. Ohne userId
     *   (nur Tests) wird der ungescopte Legacy-Key verwendet.
     */
    constructor(userId) {
        this.openTabs = []    // Projekt-UUIDs in Tab-Reihenfolge
        this.activeTab = null // Aktive Projekt-UUID
        this._storageKey = userId ? `${TABS_KEY}_${userId}` : TABS_KEY
    }

    /**
     * Lädt Tab-State aus localStorage.
     */
    load() {
        try {
            // Legacy-Key aus der Zeit vor Per-User-Scoping einmalig
            // entfernen statt übernehmen — er kann Tabs eines anderen
            // Users enthalten und darf nie in diesen Account wandern.
            if (this._storageKey !== TABS_KEY) {
                localStorage.removeItem(TABS_KEY)
            }
            const stored = localStorage.getItem(this._storageKey)
            if (stored) {
                const data = JSON.parse(stored)
                if (data.openTabs && Array.isArray(data.openTabs)) {
                    this.openTabs = data.openTabs
                    this.activeTab = data.activeTab || data.openTabs[0] || null
                }
            }
        } catch {
            // Korrupte Daten ignorieren
        }
    }

    /**
     * Speichert Tab-State in localStorage.
     */
    save() {
        try {
            localStorage.setItem(this._storageKey, JSON.stringify({
                openTabs: this.openTabs,
                activeTab: this.activeTab
            }))
        } catch {
            // Quota-Fehler ignorieren
        }
    }

    /**
     * Behält nur Tabs, deren Projekt-ID in validIds vorkommt — wirft
     * fremde (anderer User) und verwaiste (gelöschtes Projekt) Tabs raus.
     * Fällt der aktive Tab weg, wird der erste verbleibende aktiv.
     *
     * @param {Iterable<string>} validIds - IDs der eigenen, existierenden Projekte
     * @returns {boolean} true wenn Tabs entfernt wurden
     */
    retainTabs(validIds) {
        const valid = new Set(validIds)
        const kept = this.openTabs.filter(id => valid.has(id))
        if (kept.length === this.openTabs.length) return false

        this.openTabs = kept
        if (!kept.includes(this.activeTab)) {
            this.activeTab = kept[0] || null
        }
        this.save()
        return true
    }

    /**
     * Öffnet ein Projekt als Tab (oder aktiviert einen bestehenden).
     * @param {string} projectId
     */
    openTab(projectId) {
        if (!this.openTabs.includes(projectId)) {
            this.openTabs.push(projectId)
        }
        this.activeTab = projectId
        this.save()
    }

    /**
     * Schließt einen Tab.
     * @param {string} projectId
     * @returns {string|null} Die neue activeTab-ID, oder null wenn kein Tab mehr offen
     */
    closeTab(projectId) {
        const index = this.openTabs.indexOf(projectId)
        if (index === -1) return this.activeTab

        this.openTabs.splice(index, 1)

        // Wenn der aktive Tab geschlossen wurde: benachbarten Tab aktivieren
        if (this.activeTab === projectId) {
            if (this.openTabs.length === 0) {
                this.activeTab = null
            } else {
                // Nächsten Tab aktivieren (oder den letzten wenn wir am Ende waren)
                this.activeTab = this.openTabs[Math.min(index, this.openTabs.length - 1)]
            }
        }

        this.save()
        return this.activeTab
    }

    /**
     * Setzt den aktiven Tab.
     * @param {string} projectId
     */
    setActiveTab(projectId) {
        if (this.openTabs.includes(projectId)) {
            this.activeTab = projectId
            this.save()
        }
    }

    /**
     * Tauscht die UUID eines offenen Tabs an gleicher Position aus.
     * Wird vom Cloud-Off-Pfad (Spec §10.1) gerufen, weil das Projekt
     * lokal eine neue UUID bekommt, der User aber denselben Tab sehen
     * soll. No-op wenn oldId nicht offen ist.
     *
     * @param {string} oldId
     * @param {string} newId
     * @returns {boolean} true wenn ein Tab umgehaengt wurde
     */
    replaceTab(oldId, newId) {
        const idx = this.openTabs.indexOf(oldId)
        if (idx < 0) return false
        this.openTabs[idx] = newId
        if (this.activeTab === oldId) this.activeTab = newId
        this.save()
        return true
    }

    /**
     * Verschiebt einen Tab (Drag & Drop).
     * @param {number} fromIndex
     * @param {number} toIndex
     */
    reorderTab(fromIndex, toIndex) {
        if (fromIndex < 0 || fromIndex >= this.openTabs.length) return
        if (toIndex < 0 || toIndex >= this.openTabs.length) return

        const [tab] = this.openTabs.splice(fromIndex, 1)
        this.openTabs.splice(toIndex, 0, tab)
        this.save()
    }

    /**
     * Prüft ob ein Projekt als Tab offen ist.
     * @param {string} projectId
     * @returns {boolean}
     */
    isOpen(projectId) {
        return this.openTabs.includes(projectId)
    }

    /**
     * Gibt den Index eines Tabs zurück (-1 wenn nicht offen).
     * @param {string} projectId
     * @returns {number}
     */
    getTabIndex(projectId) {
        return this.openTabs.indexOf(projectId)
    }
}
